/* eslint-disable jsx-a11y/alt-text */
/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-02-25 12:21:40
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { Icon } from '@shopify/polaris';
import classnames from 'classnames';
import { ArrowRightMinor, CancelSmallMinor } from '@shopify/polaris-icons';
import ChevronUpMinor from 'src/assets/images/common/chevron-up.svg?react';
import ChevronDownMinor from 'src/assets/images/common/chevron-down.svg?react';
import { DEVICE, MIN_FONT_SIZE, MAX_FONT_SIZE } from 'src/constants/theme-settings';
import noProductImage from 'src/assets/images/product/no-image.png';
import { formatMoney } from 'src/helpers/money-format';

type Props = {
  formValues: any,
  previewProductData: any,
  device: number,
  labelTranslation: any
};

type ComposedProps = Props & WithI18nProps;

class QuickViewPreview extends Component<ComposedProps> {
  state = {
    onHoverAtc: false,
    onHoverBin: false
  };
  renderOptionValues = (option: Array<any>, key: number) => {
    const { label, name, values = [] } = option;
    let valueOptions = [];

    if (name === 'color') {
      valueOptions = values.map((value, index) => {
        return (
          <div className="product-option__outline-color" key={index}>
            <div className="product-option__color" style={{ backgroundColor: value?.title }}></div>
          </div>
        );
      });
    } else {
      valueOptions = values.map((value, index) => {
        const textOptionChecked = classnames('product-option__text', {
          'product-option__text--selected': index === 0
        });
        return (
          <div className={textOptionChecked} key={index}>
            {value?.title}
          </div>
        );
      });
    }

    return (
      <div className="product-option" key={key}>
        <div className="product-option__title">{label}</div>
        <div className="product-option__value">{valueOptions}</div>
      </div>
    );
  };

  toggleHoverAtc = () => {
    this.setState({ onHoverAtc: !this.state.onHoverAtc });
  };

  toggleHoverBin = () => {
    this.setState({ onHoverBin: !this.state.onHoverBin });
  };

  getProductTitle = product => {
    if (_.isEmpty(product)) return '';
    const { split_product = false } = product;
    if (split_product) return product.original_title || product.title;
    return product.title;
  };

  getDefaultLabels = () => {
    const { labelTranslation } = this.props;
    const defaultValues = _(labelTranslation)
      .pick(['atcAvailableLabelV2', 'qvViewFullDetailsV2', 'buyItNowBtnLabelV2', 'qvQuantityV2'])
      .reduce(
        (acc, { key, value }) => {
          if (_.isEmpty(value)) return acc;
          acc[key] = value;
          return acc;
        },
        {
          atcAvailableLabelV2: 'Quick view',
          qvViewFullDetailsV2: 'View full details',
          buyItNowBtnLabelV2: 'Buy it now',
          qvQuantityV2: 'Quantity'
        }
      );

    return defaultValues;
  };

  getValidFontSize = fontSize => {
    if (!fontSize || fontSize < MIN_FONT_SIZE || fontSize > MAX_FONT_SIZE) return 14;

    return fontSize;
  };

  render() {
    const { formValues, previewProductData = {}, device, isExpandFullscreen } = this.props;
    const { shop = {}, products: previewProducts = [] } = previewProductData;
    const defaultLabels = this.getDefaultLabels();
    const product = _.head(previewProducts) || {};
    const { quickView_setting = {} } = formValues;
    let { images_info } = product;
    if (_.isEmpty(images_info)) {
      images_info = Array(1).fill(0);
    }
    const shouldShowPagination = _.size(images_info) > 4;
    images_info = _.slice(images_info, 0, 4);

    const { showProductImage, thumbnailPosition = 'topLeft' } = quickView_setting;
    const mainImage = _.head(images_info);
    const paginationClasses = classnames('product-gallery__pagination-wrapper', {
      visible: shouldShowPagination
    });
    const popupMode =
      device === DEVICE.MOBILE ? 'mobile' : !isExpandFullscreen ? 'collapse' : 'expand';
    const galleryMode =
      device === DEVICE.MOBILE
        ? 'vertical'
        : thumbnailPosition === 'topLeft'
        ? 'horizontal'
        : 'vertical';
    const hasOptions = _.size(product?.options_with_values) > 0;
    const showBuyNowBtn = quickView_setting?.buyItNowBtn?.enable;
    const buttonShape = quickView_setting?.buttonOverall?.shape;
    const fontStyle = quickView_setting?.buttonOverall?.fontStyle;
    const fontStyleCss = fontStyle?.split('-')[0] || 'normal';
    const fontWeightCss = fontStyle?.split('-')[1] || '400';

    const btnClasses = classnames('buttons__item', {
      'buttons__item--rounded': buttonShape === 'round'
    });
    const atcTextTransform = quickView_setting?.addToCartBtn?.textTransform || 'none';
    const buyNowTextTransform = quickView_setting?.buyItNowBtn?.textTransform || 'none';
    const fontSizeBtnText = this.getValidFontSize(quickView_setting?.buttonOverall?.fontSize);
    const atcTextColor = quickView_setting?.addToCartBtn?.color;
    const atcTextHoverColor = quickView_setting?.addToCartBtn?.hoverColor;
    const atcBackgroundColor = quickView_setting?.addToCartBtn?.backgroundColor;
    const atcHoverBackgroundColor = quickView_setting?.addToCartBtn?.hoverBackgroundColor;
    const atcBorderColor = quickView_setting?.addToCartBtn?.borderColor;
    const atcHoverBorderColor = quickView_setting?.addToCartBtn?.hoverBorderColor;
    const binTextColor = quickView_setting?.buyItNowBtn?.color;
    const binTextHoverColor = quickView_setting?.buyItNowBtn?.hoverColor;
    const binBackgroundColor = quickView_setting?.buyItNowBtn?.backgroundColor;
    const binHoverBackgroundColor = quickView_setting?.buyItNowBtn?.hoverBackgroundColor;
    const binBorderColor = quickView_setting?.buyItNowBtn?.borderColor;
    const binHoverBorderColor = quickView_setting?.buyItNowBtn?.hoverBorderColor;
    const buyItNowText =
      quickView_setting?.buyItNowBtn?.text || defaultLabels['buyItNowBtnLabelV2'];

    const isDefaultOption =
      product?.options_with_values?.length === 1 &&
      product?.options_with_values?.[0]?.values?.[0]?.title === 'Default Title';

    return (
      <div className="visual-editor-preview__overlay">
        <div className={`quick-view-popup quick-view-popup--${popupMode}`}>
          {showProductImage && (
            <div className={`product-gallery product-gallery--${galleryMode}`}>
              <div className="product-gallery__thumbnails-wrapper">
                <div className={paginationClasses}>
                  <span className="Polaris-Icon">
                    <ChevronUpMinor className="Polaris-Icon__Svg" />
                  </span>
                </div>
                <div className="product-gallery__thumbnails">
                  {images_info.map((image, index) => {
                    const singleThumbnailClasses = classnames('product-gallery__single-thumbnail', {
                      'product-gallery__single-thumbnail--bordered': index === 0
                    });
                    return (
                      <img
                        className={singleThumbnailClasses}
                        key={index}
                        src={image.src || noProductImage}
                        alt={image.alt}
                      />
                    );
                  })}
                </div>
                <div className={paginationClasses}>
                  <span className="Polaris-Icon">
                    <ChevronDownMinor className="Polaris-Icon__Svg" />
                  </span>
                </div>
              </div>
              <img
                className="product-gallery__single-photo"
                src={mainImage?.src || noProductImage}
                alt={mainImage?.alt}
              />
            </div>
          )}
          <div className="product-information">
            <div className="product-information__name">{this.getProductTitle(product)}</div>
            <div className="product-information__description">{product?.vendor}</div>
            <div className="product-information__price">
              {formatMoney(product?.price_min, shop?.moneyFormat)}
            </div>

            {/* OPTION VALUES */}
            {!isDefaultOption && (
              <div className="product-information__options">
                {hasOptions &&
                  product.options_with_values.map((option, index) =>
                    this.renderOptionValues(option, index)
                  )}
              </div>
            )}

            {/* QUANTITY COUNTER */}
            {!_.isEmpty(product) && (
              <div className="quantity-counter">
                <div className="quantity-counter__title">{defaultLabels['qvQuantityV2']}</div>
                <div className="quantity-counter__counter">
                  <div>-</div>
                  <div>1</div>
                  <div>+</div>
                </div>
              </div>
            )}

            {/* BUTTON ACTION */}
            <div className="buttons">
              <div
                className={btnClasses}
                style={{
                  textTransform: atcTextTransform,
                  fontSize: fontSizeBtnText,
                  color: this.state.onHoverAtc ? atcTextHoverColor : atcTextColor,
                  backgroundColor: this.state.onHoverAtc
                    ? atcHoverBackgroundColor
                    : atcBackgroundColor,
                  borderColor: this.state.onHoverAtc ? atcHoverBorderColor : atcBorderColor,
                  fontStyle: fontStyleCss,
                  fontWeight: fontWeightCss
                }}
                onMouseEnter={this.toggleHoverAtc}
                onMouseLeave={this.toggleHoverAtc}>
                {defaultLabels['atcAvailableLabelV2']}
              </div>
              {showBuyNowBtn && (
                <div
                  className={btnClasses}
                  style={{
                    textTransform: buyNowTextTransform,
                    fontSize: fontSizeBtnText,
                    color: this.state.onHoverBin ? binTextHoverColor : binTextColor,
                    backgroundColor: this.state.onHoverBin
                      ? binHoverBackgroundColor
                      : binBackgroundColor,
                    borderColor: this.state.onHoverBin ? binHoverBorderColor : binBorderColor,
                    fontStyle: fontStyleCss,
                    fontWeight: fontWeightCss
                  }}
                  onMouseEnter={this.toggleHoverBin}
                  onMouseLeave={this.toggleHoverBin}>
                  {buyItNowText}
                </div>
              )}
            </div>

            {/* VIEW FULL DETAILS */}
            {device === DEVICE.DESKTOP && (
              <div className="hyperlink">
                <div className="hyperlink__url">{defaultLabels['qvViewFullDetailsV2']}</div>
                <Icon source={ArrowRightMinor} color="base" />
              </div>
            )}
          </div>
          <div className="close-popup-btn">
            <Icon source={CancelSmallMinor} color="base" />
          </div>
        </div>
      </div>
    );
  }
}

export default withI18n()(QuickViewPreview);
